import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import {
  MAX_CUSTOM_REMINDER_OFFSETS,
  MAX_PRESET_REMINDER_OFFSETS,
  MAX_REMINDER_OFFSET_MINUTES,
  MIN_REMINDER_OFFSET_MINUTES,
} from "./reminder-settings";
import { customTimingToMinutes, formatReminderOffset } from "./reminder-timing";

export function ReminderTimingControl({
  id,
  title,
  description,
  value,
  allowedOffsets,
  timingLabel = "Before the deadline",
  disabled = false,
  onChange,
  onConstraint,
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("minutes");
  const customOffsets = useMemo(
    () => value.offsetsMinutes.filter((offset) => !allowedOffsets.includes(offset)),
    [allowedOffsets, value.offsetsMinutes]
  );

  const removeOffset = (offset) => {
    if (value.enabled && value.offsetsMinutes.length === 1) {
      onConstraint(`${title} needs at least one timing while enabled.`);
      return;
    }
    onConstraint("");
    onChange({
      ...value,
      offsetsMinutes: value.offsetsMinutes.filter((item) => item !== offset),
    });
  };

  const addOffset = (offset, { fromCustomComposer = false } = {}) => {
    if (value.offsetsMinutes.includes(offset)) {
      onConstraint(`${formatReminderOffset(offset)} before is already included.`);
      return false;
    }

    const isPreset = allowedOffsets.includes(offset);
    const selectedPresetCount = value.offsetsMinutes.filter((item) =>
      allowedOffsets.includes(item)
    ).length;
    if (isPreset && selectedPresetCount >= MAX_PRESET_REMINDER_OFFSETS) {
      onConstraint(`${title} can use up to ${MAX_PRESET_REMINDER_OFFSETS} preset timings.`);
      return false;
    }
    if (!isPreset && customOffsets.length >= MAX_CUSTOM_REMINDER_OFFSETS) {
      onConstraint(`${title} can use up to ${MAX_CUSTOM_REMINDER_OFFSETS} additional alerts.`);
      return false;
    }

    onConstraint("");
    onChange({
      ...value,
      offsetsMinutes: [...value.offsetsMinutes, offset].sort((a, b) => b - a),
    });
    if (fromCustomComposer) {
      setAmount("");
      setCustomOpen(false);
    }
    return true;
  };

  const togglePreset = (offset) => {
    if (value.offsetsMinutes.includes(offset)) {
      removeOffset(offset);
      return;
    }
    addOffset(offset);
  };

  const handleCustomAdd = () => {
    const offset = customTimingToMinutes(amount, unit);
    if (offset === null) {
      onConstraint("Enter a whole number greater than zero for the custom alert.");
      return;
    }
    if (offset < MIN_REMINDER_OFFSET_MINUTES || offset > MAX_REMINDER_OFFSET_MINUTES) {
      onConstraint("Custom alerts must be between 10 minutes and 30 days before.");
      return;
    }
    addOffset(offset, { fromCustomComposer: true });
  };

  return (
    <fieldset className="rounded-2xl border border-slate-200/70 p-3 dark:border-slate-700">
      <legend className="sr-only">{title}</legend>
      <div className="flex items-start justify-between gap-4">
        <div>
          <label htmlFor={`${id}-enabled`} className="text-sm font-medium text-slate-800 dark:text-slate-100">
            {title}
          </label>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{description}</p>
        </div>
        <Switch
          id={`${id}-enabled`}
          checked={value.enabled}
          onCheckedChange={(enabled) => {
            onConstraint("");
            onChange({
              ...value,
              enabled,
              offsetsMinutes:
                enabled && value.offsetsMinutes.length === 0
                  ? [allowedOffsets[0]]
                  : value.offsetsMinutes,
            });
          }}
          disabled={disabled}
          aria-label={`${title} enabled`}
        />
      </div>

      {value.enabled && (
        <div className="mt-3" aria-label={`${title} timing`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {timingLabel}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {allowedOffsets.map((offset) => {
              const selected = value.offsetsMinutes.includes(offset);
              return (
                <button
                  key={offset}
                  type="button"
                  aria-pressed={selected}
                  aria-label={`${title}: ${formatReminderOffset(offset)} before`}
                  disabled={disabled}
                  onClick={() => togglePreset(offset)}
                  className={`min-h-[40px] rounded-full border px-3 py-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                    selected
                      ? "border-brand-accent bg-brand-accent/10 text-brand-primary dark:text-violet-200"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`}
                >
                  {formatReminderOffset(offset)}
                </button>
              );
            })}
          </div>

          {customOffsets.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" aria-label={`${title} additional alerts`}>
              {customOffsets.map((offset) => (
                <span
                  key={offset}
                  className="inline-flex min-h-[36px] items-center gap-1 rounded-full border border-violet-200 bg-violet-50 pl-3 pr-1.5 text-xs font-semibold text-violet-800 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200"
                >
                  {formatReminderOffset(offset)}
                  <button
                    type="button"
                    onClick={() => removeOffset(offset)}
                    disabled={disabled}
                    aria-label={`Remove ${formatReminderOffset(offset)} alert from ${title}`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:opacity-50 dark:hover:bg-violet-900/50"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {!customOpen ? (
            <button
              type="button"
              onClick={() => {
                onConstraint("");
                setCustomOpen(true);
              }}
              disabled={disabled || customOffsets.length >= MAX_CUSTOM_REMINDER_OFFSETS}
              className="mt-3 inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-2 text-xs font-semibold text-violet-700 hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:cursor-not-allowed disabled:text-slate-400 dark:text-violet-200 dark:hover:bg-violet-950/30"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {customOffsets.length >= MAX_CUSTOM_REMINDER_OFFSETS
                ? "4 additional alerts added"
                : "Add custom alert"}
            </button>
          ) : (
            <div className="mt-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="min-w-0 flex-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Alert me
                  <input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleCustomAdd();
                      }
                    }}
                    aria-label={`${title} custom alert amount`}
                    placeholder="15"
                    autoFocus
                    className="mt-1 min-h-[40px] w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800 focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                </label>
                <label className="min-w-0 flex-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Unit
                  <select
                    value={unit}
                    onChange={(event) => setUnit(event.target.value)}
                    aria-label={`${title} custom alert unit`}
                    className="mt-1 min-h-[40px] w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800 focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  >
                    <option value="minutes">minutes before</option>
                    <option value="hours">hours before</option>
                    <option value="days">days before</option>
                  </select>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCustomAdd}
                    className="min-h-[40px] rounded-full bg-brand-primary px-4 text-xs font-semibold text-white hover:bg-brand-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomOpen(false);
                      setAmount("");
                      onConstraint("");
                    }}
                    className="min-h-[40px] rounded-full px-3 text-xs font-semibold text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                Choose 10 minutes to 30 days before. Delivery times are approximate.
              </p>
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}
